import { useEffect, useRef, useState } from "react";
import { Send, Mic, MicOff, X, Volume2, VolumeX, Sparkles } from "lucide-react";
import { getLocalReply } from "@/lib/chatbot";

const GREETING = "Hey there! I'm Savi, the AI assistant from Digi Savvy. We help builders book flats, floors and plots faster. How can I help with your real-estate marketing today?";

function uid() {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ============== AI Avatar (animated SVG face) ==============
function Avatar({ size = 64, speaking = false, listening = false }) {
  return (
    <div
      data-testid="ai-avatar"
      className="relative flex items-center justify-center"
      style={{ width: size, height: size }}
    >
      <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#d4ff4f] to-emerald-400" />
      {(speaking || listening) && (
        <div className={`absolute inset-0 rounded-full ${listening ? "bg-red-400/50" : "bg-[#d4ff4f]/60"} blur-xl ${speaking ? "animate-pulse" : ""}`} />
      )}
      <svg viewBox="0 0 64 64" className="relative" width={size} height={size}>
        <defs>
          <radialGradient id="face" cx="50%" cy="40%">
            <stop offset="0%" stopColor="#e8ffb0" />
            <stop offset="100%" stopColor="#9ed119" />
          </radialGradient>
        </defs>
        <circle cx="32" cy="32" r="28" fill="url(#face)" />
        {/* Eyes */}
        <g className="eye-blink">
          <ellipse cx="23" cy="28" rx="2.5" ry="3.5" fill="#0a0b08" />
          <ellipse cx="41" cy="28" rx="2.5" ry="3.5" fill="#0a0b08" />
          <circle cx="23.7" cy="27" r="0.9" fill="white" />
          <circle cx="41.7" cy="27" r="0.9" fill="white" />
        </g>
        {/* Mouth */}
        {speaking ? (
          <ellipse cx="32" cy="42" rx="6" ry="3" fill="#0a0b08" className="mouth-talk" />
        ) : listening ? (
          <circle cx="32" cy="42" r="2.5" fill="#0a0b08" />
        ) : (
          <path d="M 24 41 Q 32 47 40 41" stroke="#0a0b08" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        )}
        {/* Headset hint */}
        <path d="M 10 30 Q 10 12 32 12 Q 54 12 54 30" stroke="#0a0b08" strokeWidth="2" fill="none" opacity="0.4" />
      </svg>
    </div>
  );
}

// Equalizer when speaking
function Equalizer({ active }) {
  return (
    <div className="flex items-end gap-0.5 h-5">
      {[0, 1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className="w-1 rounded-full bg-[#d4ff4f] eq-bar"
          style={{ animationDelay: `${i * 0.08}s`, height: active ? undefined : "4px", opacity: active ? 1 : 0.3 }}
        />
      ))}
    </div>
  );
}

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [bubble, setBubble] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [muted, setMuted] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [listening, setListening] = useState(false);
  const [voiceReady, setVoiceReady] = useState(false);
  const [hasGreeted, setHasGreeted] = useState(false);
  const sessionRef = useRef(uid());
  const recogRef = useRef(null);
  const scrollRef = useRef(null);
  const audioInitRef = useRef(false);

  // Pre-warm voices (some browsers load async)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const synth = window.speechSynthesis;
    if (!synth) return;
    const loadVoices = () => {
      const v = synth.getVoices();
      if (v && v.length) setVoiceReady(true);
    };
    loadVoices();
    synth.onvoiceschanged = loadVoices;
  }, []);

  // After short delay show bubble + auto-greet on first interaction
  useEffect(() => {
    const t = setTimeout(() => setBubble(true), 1800);
    return () => clearTimeout(t);
  }, []);

  // Auto-greet on first user interaction (browsers block autoplay TTS)
  useEffect(() => {
    const handler = () => {
      if (hasGreeted) return;
      setHasGreeted(true);
      // wait a beat so the page is visible
      setTimeout(() => {
        setMessages([{ role: "assistant", content: GREETING }]);
        setBubble(true);
        if (!muted) speak(GREETING);
      }, 500);
      window.removeEventListener("click", handler);
      window.removeEventListener("keydown", handler);
      window.removeEventListener("scroll", handler);
    };
    window.addEventListener("click", handler, { once: false });
    window.addEventListener("keydown", handler, { once: false });
    window.addEventListener("scroll", handler, { once: false });
    return () => {
      window.removeEventListener("click", handler);
      window.removeEventListener("keydown", handler);
      window.removeEventListener("scroll", handler);
    };
  }, [hasGreeted, muted]);

  // Scroll chat to bottom on new message
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  function speak(text) {
    if (typeof window === "undefined") return;
    const synth = window.speechSynthesis;
    if (!synth || muted) return;
    try {
      synth.cancel();
      const u = new SpeechSynthesisUtterance(text);
      const voices = synth.getVoices();
      // Prefer a female English voice, fallback to default
      const preferred =
        voices.find((v) => /female/i.test(v.name) && /en[-_]?/i.test(v.lang)) ||
        voices.find((v) => /(samantha|google.*english.*female|microsoft zira|microsoft aria)/i.test(v.name)) ||
        voices.find((v) => /^en/i.test(v.lang)) ||
        voices[0];
      if (preferred) u.voice = preferred;
      u.rate = 1.02;
      u.pitch = 1.05;
      u.volume = 1;
      u.onstart = () => setSpeaking(true);
      u.onend = () => setSpeaking(false);
      u.onerror = () => setSpeaking(false);
      synth.speak(u);
    } catch (e) {
      // ignore
    }
  }

  function stopSpeak() {
    try {
      window.speechSynthesis?.cancel();
    } catch (e) {}
    setSpeaking(false);
  }

  async function sendMessage(text) {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    // Simulate a small "thinking" delay for natural feel
    const delay = 600 + Math.random() * 700;
    setTimeout(() => {
      try {
        const reply = getLocalReply(msg);
        setMessages((m) => [...m, { role: "assistant", content: reply }]);
        if (!muted) speak(reply);
      } finally {
        setLoading(false);
      }
    }, delay);
  }

  function toggleListen() {
    if (typeof window === "undefined") return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      alert("Voice input isn't supported in this browser. Try Chrome or Edge, or type your message.");
      return;
    }
    if (listening) {
      try { recogRef.current?.stop(); } catch (e) {}
      setListening(false);
      return;
    }
    const recog = new SR();
    recog.lang = "en-IN";
    recog.continuous = false;
    recog.interimResults = false;
    recog.onstart = () => setListening(true);
    recog.onerror = () => setListening(false);
    recog.onend = () => setListening(false);
    recog.onresult = (e) => {
      const transcript = e.results?.[0]?.[0]?.transcript;
      if (transcript) sendMessage(transcript);
    };
    recogRef.current = recog;
    try { recog.start(); } catch (e) { setListening(false); }
  }

  function openChat() {
    setOpen(true);
    setBubble(false);
    if (messages.length === 0) {
      setMessages([{ role: "assistant", content: GREETING }]);
      if (!muted) speak(GREETING);
    }
  }

  return (
    <>
      {/* Floating launcher */}
      <div className="fixed bottom-20 right-6 z-40 flex flex-col items-end gap-3" data-testid="ai-assistant-launcher">
        {/* Pre-open bubble */}
        {bubble && !open && (
          <div data-testid="ai-bubble" className="max-w-[280px] rounded-2xl rounded-br-md border border-[#d4ff4f]/40 bg-[#0a0b08]/95 backdrop-blur p-4 shadow-2xl shadow-[#d4ff4f]/10 relative animate-in fade-in slide-in-from-bottom-3 duration-500">
            <button
              data-testid="ai-bubble-close"
              onClick={() => setBubble(false)}
              className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
              aria-label="Dismiss"
            >
              <X className="w-3 h-3" />
            </button>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-[#d4ff4f]" />
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#d4ff4f]">Savi · AI Assistant</span>
            </div>
            <p className="text-sm text-white/80 leading-relaxed">Namaste! I'm Savi 👋 Want help choosing a marketing plan for your project? <button onClick={openChat} className="text-[#d4ff4f] underline underline-offset-2">Chat with me</button>.</p>
          </div>
        )}

        {!open && (
          <button
            data-testid="ai-launch-btn"
            onClick={openChat}
            className="relative group"
            aria-label="Open AI Assistant"
          >
            <span className="absolute inset-0 rounded-full bg-[#d4ff4f]/40 blur-xl group-hover:bg-[#d4ff4f]/70 transition" />
            <span className="relative block rounded-full p-1 bg-gradient-to-br from-[#d4ff4f] to-emerald-400 pulse-ring">
              <Avatar size={56} speaking={false} />
            </span>
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 border-2 border-[#0a0b08] flex items-center justify-center text-[9px] font-bold text-white">1</span>
          </button>
        )}
      </div>

      {/* Chat panel */}
      {open && (
        <div data-testid="ai-chat-panel" className="fixed inset-x-0 bottom-0 sm:inset-auto sm:bottom-20 sm:right-6 z-50 w-full sm:w-[400px] h-[80vh] sm:h-[600px] flex flex-col rounded-t-3xl sm:rounded-3xl border border-white/10 bg-[#0a0b08]/95 backdrop-blur-xl shadow-2xl shadow-black/50 overflow-hidden animate-in slide-in-from-bottom-5 duration-300">
          {/* Header */}
          <div className="flex items-center gap-3 p-4 border-b border-white/10 bg-gradient-to-r from-[#d4ff4f]/[0.04] to-transparent">
            <div className="relative">
              <Avatar size={44} speaking={speaking} listening={listening} />
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#d4ff4f] border-2 border-[#0a0b08] animate-pulse" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-base tracking-tight leading-tight">Savi</div>
              <div className="flex items-center gap-2 text-[11px] text-white/50">
                <span className="font-mono uppercase tracking-wider">AI Assistant</span>
                <Equalizer active={speaking} />
              </div>
            </div>
            <button
              data-testid="ai-toggle-mute"
              onClick={() => { setMuted((m) => !m); if (!muted) stopSpeak(); }}
              className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center text-white/70"
              aria-label={muted ? "Unmute voice" : "Mute voice"}
              title={muted ? "Unmute voice" : "Mute voice"}
            >
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
            <button
              data-testid="ai-close-btn"
              onClick={() => { setOpen(false); stopSpeak(); }}
              className="w-9 h-9 rounded-full hover:bg-white/10 flex items-center justify-center text-white/70"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-message-${m.role}-${i}`}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                {m.role === "assistant" && (
                  <div className="shrink-0 mr-2"><Avatar size={28} /></div>
                )}
                <div className={`max-w-[78%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-[#d4ff4f] text-black rounded-br-sm"
                    : "bg-white/[0.06] text-white/90 rounded-bl-sm"
                }`}>
                  {m.content}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start" data-testid="chat-loading">
                <div className="shrink-0 mr-2"><Avatar size={28} /></div>
                <div className="px-4 py-3 rounded-2xl rounded-bl-sm bg-white/[0.06] flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d4ff4f] animate-bounce" />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d4ff4f] animate-bounce" style={{ animationDelay: "0.12s" }} />
                  <span className="w-1.5 h-1.5 rounded-full bg-[#d4ff4f] animate-bounce" style={{ animationDelay: "0.24s" }} />
                </div>
              </div>
            )}
          </div>

          {/* Suggestion chips */}
          {messages.length <= 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-2">
              {["Show me pricing", "Lead generation for a new tower", "RERA-compliant ads", "Book a Growth Call"].map((s) => (
                <button
                  key={s}
                  data-testid={`chat-chip-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => sendMessage(s)}
                  className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-white/80 hover:border-[#d4ff4f]/60 hover:bg-[#d4ff4f]/[0.08] hover:text-[#d4ff4f] transition"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <form
            onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
            className="flex items-center gap-2 p-3 border-t border-white/10 bg-black/40"
          >
            <button
              type="button"
              data-testid="ai-mic-btn"
              onClick={toggleListen}
              className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 transition ${
                listening ? "bg-red-500 text-white" : "bg-white/5 hover:bg-white/10 text-white/70"
              }`}
              aria-label={listening ? "Stop listening" : "Voice input"}
            >
              {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </button>
            <input
              data-testid="ai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={listening ? "Listening…" : "Ask anything about real estate marketing…"}
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm placeholder:text-white/40 focus:outline-none focus:border-[#d4ff4f]/40"
            />
            <button
              type="submit"
              data-testid="ai-send-btn"
              disabled={!input.trim() || loading}
              className="w-10 h-10 rounded-full bg-[#d4ff4f] text-black flex items-center justify-center disabled:opacity-40 hover:bg-white transition shrink-0"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
